import moment from "moment";
import { useCallback, useMemo } from "react";
import { SlotEvent } from "./DragNDropCalendar";
import { useEventStatus } from "./EventStatusContext";
import { TimeSlot } from "./types";


interface UseTimeslotBusinessLogicProps {
    data: any;
    setAvailability: Function;
    updateSlot: Function;
    clientCode: string | number;
    hiringManager: string | number;
}

export function useTimeslotBusinessLogic({
    data,
    setAvailability,
    updateSlot,
    clientCode,
    hiringManager,
}: UseTimeslotBusinessLogicProps) {
    // Map API data to TimeSlot[]
    const availableTimeSlotsFetched: TimeSlot[] = useMemo(() => {
        // Add or update a single timeslot

        if (!data) return [];
        return data.data.map((slot: any) => ({
            startTime: moment(slot.slotSetOn)
                .set("hours", 0)
                .set("minutes", 0)
                .add(Number(slot.slotSetAt.split(":")[0]), "hours")
                .add(Number(slot.slotSetAt.split(":")[1]) + moment().utcOffset(), "minutes")
                .toDate(),
            duration: slot.durationInMin,
            id: slot.id,
            employerName: slot.employerName,
            employerEmail: slot.employerEmail,
            isFetched: true,
            isAvailable: slot.isAvailable,
        }));
    }, [data]);

    const { dispatch } = useEventStatus();



    const addOrUpdateSingleTimeslot = useCallback(
        async (slot: SlotEvent) => {

            // Always use the base id (without ::index) for status context
            const baseId = slot.id.split("::")[0];
            const timeslot: TimeSlot = {
                duration: moment(slot.end).diff(moment(slot.start), "minutes"),
                id: Number(baseId),
                isFetched: false,
                startTime: moment(slot.start).toDate(),
                employerEmail: slot.userEmail,
                employerName: slot.userName,
                isAvailable: slot.isAvailable,
            };

            dispatch({ type: "SET_STATUS", id: slot.id, status: "loading" });
            try {
                if (slot.newSlot) {
                    await setAvailability({
                        employerEmail: timeslot.employerEmail,
                        employerName: timeslot.employerName,
                        employerId: hiringManager.toString(),
                        slots: [
                            {
                                id: timeslot.id,
                                clientId: clientCode.toString(),
                                durationInMin: timeslot.duration,
                                interviewId: 0,
                                isAvailable: true,
                                jobId: "",
                                slotSetOn: timeslot.startTime.toISOString(),
                                slotSetAt: moment(timeslot.startTime).utc(false).format("HH:mm"),
                            },
                        ],
                        offSet: `${moment.tz(moment.tz.guess()).utcOffset()}`,
                    }).unwrap();
                } else if (slot.updated && data && data.data.length) {
                    const offSet = moment.tz(moment.tz.guess()).utcOffset();
                    const findFromList = data.data.find((s: any) => s.id === timeslot.id);
                    if (findFromList) {
                        await updateSlot({
                            ...findFromList,
                            durationInMin: timeslot.duration,
                            slotSetOn: timeslot.startTime.toISOString(),
                            slotSetAt: moment(timeslot.startTime).utc(false).format("HH:mm"),
                            reschduleSlotLink: "",
                            slotId: findFromList.id,
                            offSet: `${offSet > 0 ? "+" : ""}${offSet}`,
                        }).unwrap();
                    }
                }
                dispatch({ type: "SET_STATUS", id: slot.id, status: "success" });
            } catch (e) {
                dispatch({ type: "SET_STATUS", id: slot.id, status: "error" });
            }
        },
        [clientCode, data, dispatch, hiringManager, setAvailability, updateSlot]
    );

    // Handle only new events
    const handleNewEvents = useCallback((slots: SlotEvent[]) => {
        const newTimeSlots: Array<TimeSlot> = slots
            .filter((slot) => slot.newSlot)
            .map((event) => {
                const id_ = event.id.split("::")[0];
                return {
                    duration: moment(event.end).diff(moment(event.start), "minutes"),
                    id: Number(id_),
                    isFetched: false,
                    startTime: moment(event.start).toDate(),
                    employerEmail: event.userEmail,
                    employerName: event.userName,
                    isAvailable: event.isAvailable,
                };
            });
        if (newTimeSlots.length)
            setAvailability({
                employerEmail: newTimeSlots[0].employerEmail,
                employerName: newTimeSlots[0].employerName,
                employerId: hiringManager.toString(),
                slots: newTimeSlots.map((nts) => ({
                    id: nts.id,
                    clientId: clientCode.toString(),
                    durationInMin: nts.duration,
                    interviewId: 0,
                    isAvailable: true,
                    jobId: "",
                    slotSetOn: nts.startTime.toISOString(),
                    slotSetAt: moment(nts.startTime).utc(false).format("HH:mm"),
                })),
                offSet: `${moment.tz(moment.tz.guess()).utcOffset()}`,
            });
    }, [clientCode, hiringManager, setAvailability]);

    // Handle only updated events
    const handleUpdatedEvents = useCallback((slots: SlotEvent[]) => {
        const updatedSlots: Array<TimeSlot> = slots
            .filter((slot) => !slot.newSlot && slot.updated)
            .map((event) => {
                const id_ = event.id.split("::")[0];
                return {
                    duration: moment(event.end).diff(moment(event.start), "minutes"),
                    id: Number(id_),
                    isFetched: false,
                    employerEmail: event.userEmail,
                    employerName: event.userName,
                    startTime: moment(event.start).toDate(),
                    isAvailable: event.isAvailable,
                };
            });
        if (updatedSlots.length && data && data.data.length) {
            const offSet = moment.tz(moment.tz.guess()).utcOffset();
            updatedSlots.forEach((nts) => {
                const findFromList = data.data.find((slot: any) => slot.id === nts.id);
                if (findFromList) {
                    updateSlot({
                        ...findFromList,
                        durationInMin: nts.duration,
                        slotSetOn: nts.startTime.toISOString(),
                        slotSetAt: moment(nts.startTime).utc(false).format("HH:mm"),
                        reschduleSlotLink: "",
                        slotId: findFromList.id,
                        offSet: `${offSet > 0 ? "+" : ""}${offSet}`,
                    });
                }
            });
        }
    }, [data, updateSlot]);

    // Encapsulate booking logic
    const handleBookTimeslot = useCallback((slots: SlotEvent[]) => {
        handleUpdatedEvents(slots);
        handleNewEvents(slots);
    }, [handleUpdatedEvents, handleNewEvents]);

    return { availableTimeSlotsFetched, handleBookTimeslot, handleNewEvents, handleUpdatedEvents, addOrUpdateSingleTimeslot };
}
